/**
 * Lightweight error reporter that sends errors to a Supabase edge function.
 * Debounces identical errors and silently fails — must never crash the app.
 */

import { supabase } from '@/integrations/supabase/client';

interface ErrorContext {
  component?: string;
  [key: string]: unknown;
}

// Track recently reported errors to debounce duplicates
const recentErrors = new Map<string, number>();
const DEBOUNCE_MS = 10_000; // Don't report the same error within 10s

function getErrorKey(message: string, component?: string): string {
  return `${component || ''}:${message}`;
}

/**
 * Browser-extension errors surface on our window (content scripts that wrap
 * fetch/XHR, inject code, etc.) but are NOT our bugs — they're pure noise that
 * inflates the daily error digest (e.g. "Failed to fetch" thrown inside an
 * extension's wrapped fetch). Drop anything whose ORIGINATING frame (the top of
 * the stack) is an extension scheme; only the originating frame is checked so a
 * genuine app error that merely passed through an extension-wrapped API is still
 * reported. Also drop the detail-less cross-origin "Script error.".
 */
function isThirdPartyNoise(message: string, stack?: string): boolean {
  if (message === 'Script error.' && !stack) return true;
  if (!stack) return false;
  const firstFrame = stack
    .split('\n')
    .find((l) => /\s(at|@)\s|^\s*at\s|@http|@chrome|@moz/.test(l) || /-extension:\/\//.test(l));
  return !!firstFrame && /(chrome|moz|safari-web|safari|ms-browser)-extension:\/\//.test(firstFrame);
}

/**
 * Report an error to the backend. Safe to call from anywhere —
 * never throws, never blocks, never crashes.
 */
export async function reportError(
  error: unknown,
  context?: ErrorContext,
): Promise<void> {
  try {
    const message =
      error instanceof Error ? error.message : String(error);
    const stack =
      error instanceof Error ? error.stack : undefined;
    const component = context?.component;

    // The daily digest is for PRODUCTION user errors only. Dev builds (and
    // locally/LAN-served builds) emit transient errors during active
    // development — HMR module-shape changes, half-saved edits, "Importing
    // binding X is not found", "Rendered more hooks…" mid-edit — which are seen
    // live in the console and must NOT pollute the prod digest. Gate on the dev
    // build flag plus localhost / private-LAN hostnames (covers a prod build
    // served via `vite preview` on the LAN during phone testing).
    if (import.meta.env.DEV) return;
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    if (
      /^(localhost|127\.0\.0\.1|0\.0\.0\.0|::1)$/.test(host) ||
      /^(10|192\.168)\./.test(host) ||
      /^172\.(1[6-9]|2\d|3[01])\./.test(host)
    ) {
      return;
    }

    // Drop browser-extension / cross-origin noise before it hits the digest.
    if (isThirdPartyNoise(message, stack)) return;

    // Debounce identical errors
    const key = getErrorKey(message, component);
    const lastReported = recentErrors.get(key);
    if (lastReported && Date.now() - lastReported < DEBOUNCE_MS) {
      return;
    }
    recentErrors.set(key, Date.now());

    // Prune old entries to prevent memory leak. Two-stage: first drop
    // anything past the debounce window, then if everything is "fresh" enforce
    // a hard cap by evicting the oldest insertion-ordered entries. Without
    // the hard cap a runaway loop firing 200 distinct errors inside the
    // debounce window would grow the map unbounded.
    if (recentErrors.size > 100) {
      const cutoff = Date.now() - DEBOUNCE_MS;
      for (const [k, v] of recentErrors) {
        if (v < cutoff) recentErrors.delete(k);
      }
      while (recentErrors.size > 100) {
        const oldestKey = recentErrors.keys().next().value;
        if (oldestKey === undefined) break;
        recentErrors.delete(oldestKey);
      }
    }

    // Build payload
    const payload = {
      error_message: message,
      error_stack: stack,
      component,
      context: context ? { ...context, component: undefined } : {},
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      user_agent:
        typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    };

    // Get auth token if available (don't block on auth)
    const session = await supabase.auth.getSession();
    const token = session?.data?.session?.access_token;

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

    if (!supabaseUrl || !apikey) return;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      apikey,
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Fire and forget — don't await in most callers
    fetch(`${supabaseUrl}/functions/v1/log-error`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    }).catch(() => {
      // Silently ignore — error reporter itself must not crash
    });
  } catch {
    // Silently ignore any error in the reporter itself
  }
}

/**
 * Install a global handler for unhandled promise rejections.
 * Call once in main.tsx.
 */
export function installGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, { component: 'unhandledrejection' });
  });

  window.addEventListener('error', (event) => {
    reportError(event.error || event.message, { component: 'window.onerror' });
  });
}
